// Angular
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
// AG-Grid
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { Router } from '@angular/router';
import { StateModel } from '../model/state-status.model';
import { OrderInboxService } from '../service/order-inbox.service';
import { BroadcastEventModel, BroadcastService, PermissionService, ToasterType } from '@cv-invision/core';
import { RESUBMIT_ORDER_EVENT } from '../resubmit-order/resubmit-order.component';
import { Store } from '@ngxs/store';
import { INVALID_DATA_ORDER } from '../model/invalid-data-order-state.model';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { CleanQualityOrder } from '../../quality/state/actions/quality.actions';
import { DialogService } from 'primeng/dynamicdialog';
import { ReOpenDialogComponent } from '../dialogs/reopen-action/reopen-dialog.component';
import { PERMISSION } from '../model/permission.model';
import { ACTION_TOOLTIPS } from '../model/tooltips.model';
import { CUScoreOverwriteDialogComponent } from '../dialogs/cu-score-overwrite/cu-score-overwrite-dialog.component';
import { ConfirmationService, MessageService } from 'primeng/api';

@Component({
	selector: 'cv-custom-action-cell',
	templateUrl: './custom-action-cell.component.html',
	styleUrls: ['./custom-action-cell.component.scss'],
})
export class CustomActionCellComponent implements OnInit, OnDestroy, ICellRendererAngularComp {
	cellValue: any;
	dataCell: any;
	canReopen: boolean;
	canResubmit: boolean;
	clientReferenceId: number;
	actionsDisabled = false;
	loadingOrder = false;
	actionTooltips = { ...ACTION_TOOLTIPS };

	get goToOrderMessage(): string {
		let response = ACTION_TOOLTIPS.ACCESS;
		if (this.actionsDisabled) {
			response = ACTION_TOOLTIPS.DISABLED;
		}
		return response;
	}

	private atComponentDestroy = new Subject();

	constructor(
		private ngZone: NgZone,
		private router: Router,
		private store: Store,
		private inboxService: OrderInboxService,
		private broadcastService: BroadcastService,
		private dialogService: DialogService,
		private userPermision: PermissionService,
		private confirmationService: ConfirmationService,
		private messagesService: MessageService
	) {}

	ngOnInit(): void {
		this.loadingOrder = false;
		this.broadcastService
			.onEvent(INVALID_DATA_ORDER)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((broadcast: BroadcastEventModel) => {
				this.loadingOrder = false;
			});
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * When the component renders, it got the possible value
	 * @param params
	 */
	agInit(params: ICellRendererParams): void {
		const { data } = params;
		this.cellValue = params;
		if (data.stateEnum === StateModel.Approved && data.schema !== 'InspectionReport:3') {
			this.canReopen = true;
		}
		this.canResubmit = data.stateEnum === StateModel.Rejected;
		this.clientReferenceId = data.externalId;
		if (data.isWorkable === false) {
			this.actionsDisabled = true;
		}
		// will keep this commented code for reference with Az
		// if (data.smartQCState === 1 || data.guaranteeState === 1 || data.isWorkable === false) {
		// 	this.actionsDisabled = true;
		// }
	}

	/**
	 * Put back the value
	 * @param params
	 */
	refresh(params: ICellRendererParams): boolean {
		this.cellValue = params;
		return true;
	}

	/**
	 * open rejection box
	 */
	toggleReSubmitBox(): void {
		if (!this.canResubmit || this.actionsDisabled) {
			return;
		}
		const { data } = this.cellValue;
		this.broadcastService.broadcast({
			name: RESUBMIT_ORDER_EVENT,
			event: {
				isResubmit: true,
				data: data,
			},
		});
	}

	/**
	 * Checks if the user has the permission to override cu score and the external provider is accepted.
	 * @returns {boolean}
	 */
	validateOverWriteVisibility() {
		const externalProvider = this.cellValue?.data?.externalTrackingProvider;
		if (!externalProvider) return false;
		const isValidExternalProvider = externalProvider !== 'AppScope' && externalProvider !== 'Manual';
		return this.userPermision.hasPermission(PERMISSION.UCDP_OVERWRITER) && isValidExternalProvider;
	}

	/**
	 * Opens the Overwrite CU Score dialog.
	 */
	openCUDialog() {
		const { data } = this.cellValue;
		this.dialogService.open(CUScoreOverwriteDialogComponent, {
			width: '960px',
			header: 'Set Manual UCDP/EAD Data',
			data: {
				orderId: data?.id,
				documentId: data?.documentId,
			},
			styleClass: 'reopen-dialog ucdp-override-dialog',
		});
	}

	/**
	 * re opnen an order
	 */
	reOpen(): void {
		if (!this.canReopen || this.actionsDisabled) {
			return;
		}
		const { data } = this.cellValue;
		this.dialogService
			.open(ReOpenDialogComponent, {
				width: '718px',
				height: '400px',
				header: 'Reopen Order',
				data: {
					orderId: data?.id,
				},
				styleClass: 'reopen-dialog',
			})
			.onClose.subscribe((result) => {
				if (result === 'redirect') {
					this.ngZone.run(() => {
						setTimeout(() => {
							this.router.navigate([`/intelligence/quality/initial`, data.id]);
						}, 500);
					});
				}
			});
	}

	/**
	 * Returns a name initials (firstname + lastname)
	 */
	getNameInitials(): any {
		const lockedBy = this.cellValue.data.lockedBy;
		return lockedBy
			? `${lockedBy.firstName.charAt(0).toUpperCase()}${lockedBy.lastName.charAt(0).toUpperCase()}`
			: '';
	}

	/**
	 * Returns lock by title
	 */
	getLockTitle(): string {
		if (this.cellValue.data.locked && this.cellValue.data.lockedBy) {
			return `Locked by ${this.cellValue.data.lockedBy.firstName} ${this.cellValue.data.lockedBy.lastName}`;
		}
		return '';
	}

	/**
	 * redirect to the selected order detail page
	 */
	openOrder(): void {
		if (this.actionsDisabled) {
			return;
		}
		const { data } = this.cellValue;
		if (data && !this.loadingOrder) {
			this.loadingOrder = true;
			this.ngZone.run(async () => {
				await this.store.dispatch(new CleanQualityOrder()).toPromise();
				setTimeout(async () => {
					this.router.navigate([`/intelligence/quality/initial`, data.id]);
				}, 50);
			});
		}
	}

	/**
	 * Resolves an order by cell value order id
	 */
	async resolveOrder(): Promise<void> {
		const { data } = this.cellValue;
		this.confirmationService.confirm({
			message: `Are you sure that you want to start a resolve process for order ${data.externalId}"?`,
			icon: 'pi pi-exclamation-triangle',
			accept: async () => {
				try {
					await lastValueFrom(this.inboxService.resolveQueuesByOrderId(data.id));
					this.messagesService.add({
						severity: 'success',
						summary: 'Success',
						detail: 'The resolve process was successfully started, this might take a couple minutes.',
					});
				} catch (error) {
					console.error(error);
				}
			},
		});
	}
}
